import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  createClientId,
  createMotionAmplificationUpload,
  type MotionAmplificationJobId,
  type MotionAmplificationParameters,
  type MotionAmplificationPreset,
  type MotionAmplificationRawVideo,
  reportMotionAmplificationJobFailure,
  startMotionAmplificationJob,
  uploadMotionAmplificationVideo,
} from "../clients/motion-amplification";
import { motionAmplificationJobsListKey } from "../queries/useMotionAmplificationJobsQuery";
import { motionAmplificationRawVideosQueryKey } from "../queries/useMotionAmplificationRawVideosQuery";

type MotionAmplificationSubmissionDetails = {
  name: string;
  description: string;
  preset: MotionAmplificationPreset;
  parameters: MotionAmplificationParameters;
  fps: number | null;
  width?: number | null;
  height?: number | null;
};

export type MotionAmplificationSubmission =
  | (MotionAmplificationSubmissionDetails & {
      file: File;
      existingVideo: null;
    })
  | (MotionAmplificationSubmissionDetails & {
      file: null;
      existingVideo: MotionAmplificationRawVideo;
    });

export type ActiveMotionAmplificationUpload = {
  jobId: MotionAmplificationJobId;
  /** 0-100. Reaches 100 once the S3 PUT (or the "existing video" no-op) finishes. */
  progress: number;
};

const motionAmplificationUploadMutationKey = [
  "motion-amplification",
  "upload",
] as const;

/**
 * Orchestrates create -> S3 PUT -> start. Its mutation lifecycle refreshes
 * the jobs list on completion and raw videos only after a new file upload.
 * `activeUpload` tracks only client-side S3 upload percentage.
 */
export function useUploadMotionAmplificationVideoMutation() {
  const queryClient = useQueryClient();
  const [activeUpload, setActiveUpload] =
    useState<ActiveMotionAmplificationUpload | null>(null);
  const isMounted = useRef(true);
  const uploadAbortController = useRef<AbortController | null>(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      uploadAbortController.current?.abort();
    };
  }, []);

  function updateActiveUpload(value: ActiveMotionAmplificationUpload | null) {
    if (isMounted.current) setActiveUpload(value);
  }

  function cancelUpload() {
    uploadAbortController.current?.abort();
  }

  const mutation = useMutation({
    mutationKey: motionAmplificationUploadMutationKey,
    mutationFn: async ({
      file,
      existingVideo,
      name,
      description,
      preset,
      parameters,
      fps,
      width,
      height,
    }: MotionAmplificationSubmission): Promise<MotionAmplificationJobId> => {
      if ((file === null) === (existingVideo === null)) {
        throw new Error(
          "Provide either a new video file or an existing video.",
        );
      }

      const fileName = existingVideo
        ? (existingVideo.fileName ?? "motion-amplification-video.mp4")
        : file.name;
      // Keep this key for every request made while handling this one submission.
      const idempotencyKey = createClientId();
      const upload = await createMotionAmplificationUpload(
        {
          source: existingVideo ? "existing" : "upload",
          name: name.trim(),
          description: description.trim() || null,
          fileName,
          fileSize: existingVideo ? existingVideo.fileSize : file.size,
          contentType: existingVideo
            ? existingVideo.contentType
            : file.type || "video/mp4",
          rawObjectKey: existingVideo?.rawObjectKey,
          preset,
          ...(fps !== null ? { fps } : {}),
          ...(typeof width === "number" && typeof height === "number"
            ? { width, height }
            : {}),
          parameters,
        },
        idempotencyKey,
      );
      const jobId = upload.jobId;
      updateActiveUpload({ jobId, progress: file ? 0 : 100 });

      let startRequested = false;
      try {
        if (file && !upload.uploadUrl) {
          throw new Error("The upload URL was not returned by the API.");
        }
        if (file && upload.uploadUrl) {
          const abortController = new AbortController();
          uploadAbortController.current = abortController;
          try {
            await uploadMotionAmplificationVideo(
              upload.uploadUrl,
              file,
              (progress) => updateActiveUpload({ jobId, progress }),
              { signal: abortController.signal },
            );
          } finally {
            if (uploadAbortController.current === abortController) {
              uploadAbortController.current = null;
            }
          }
        }
        updateActiveUpload({ jobId, progress: 100 });
        startRequested = true;
        await startMotionAmplificationJob(jobId);
      } catch (uploadError: unknown) {
        const errorMessage =
          uploadError instanceof Error
            ? uploadError.message
            : "Unable to upload or start Motion Amplification.";
        // Once start has been requested, the BFF owns the job's terminal state.
        // Reporting here could overwrite or duplicate a failure it already stored.
        if (!startRequested) {
          await reportMotionAmplificationJobFailure(jobId, {
            phase: "S3 upload",
            errorMessage,
          }).catch(() => undefined);
        }
        throw uploadError;
      } finally {
        updateActiveUpload(null);
      }
      return jobId;
    },
    onSuccess: async (_jobId, submission) => {
      if (submission.file) {
        await queryClient.invalidateQueries({
          queryKey: motionAmplificationRawVideosQueryKey,
        });
      }
      toast.add("Motion amplification processing started.");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to upload or start Motion Amplification.",
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: motionAmplificationJobsListKey,
      });
    },
  });

  return {
    ...mutation,
    uploadVideo: mutation.mutateAsync,
    cancelUpload,
    activeUpload,
  };
}
